import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";
import { FEATURES, PRODUCT_SHOTS, PRODUCT_VIDEOS } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

const ACCENT_GRAD = {
  backgroundImage: "linear-gradient(90deg, #00C2D1 0%, #2EE59D 100%)",
};

function FeatureTitle({ feature, className }) {
  if (!feature.titleParts) {
    return <span className={className}>{feature.title}</span>;
  }
  return (
    <span className={className}>
      {feature.titleParts.map((p, i) => {
        if (p.break) return <br key={`br-${i}`} />;
        if (p.accent) {
          return (
            <span
              key={i}
              className="bg-clip-text text-transparent"
              style={ACCENT_GRAD}
            >
              {p.text}
            </span>
          );
        }
        return <span key={i}>{p.text}</span>;
      })}
    </span>
  );
}

function MediaFrame({ feature }) {
  const video = PRODUCT_VIDEOS[feature.img];
  const image = PRODUCT_SHOTS[feature.img];
  return (
    <div className="relative bg-white product-frame overflow-hidden rounded-md">
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b hairline bg-[#F7F8FA]">
        <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]/70" />
        <span className="ml-3 text-[11px] text-[#1A3A61]/50 font-medium">
          app.licito.guru / {feature.id}
        </span>
      </div>
      {video ? (
        <video
          key={video}
          src={video}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          poster={image}
          className="w-full h-auto block bg-white"
        />
      ) : (
        <img
          src={image}
          alt={feature.title}
          className="w-full h-auto block"
          loading="lazy"
        />
      )}
    </div>
  );
}

/**
 * Features — sits directly under the hero.
 * No section header. Left: tab list; Right panel: text alongside the product image.
 * Mobile: accordion.
 */
export default function Features() {
  const [active, setActive] = useState(0);
  const feature = FEATURES[active] || FEATURES[0];

  return (
    <section
      id="funcionalidades"
      data-testid="features-section"
      className="relative env-white pb-24 md:pb-32"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-8">
        {/* Section heading */}
        <div className="mb-10 md:mb-14 pt-6 md:pt-10">
          <span className="kicker">Funcionalidades</span>
        </div>

        {/* Desktop: horizontal tab nav on top, then large media + text below */}
        <div className="hidden md:block">
          {/* Horizontal tabs — equally distributed, no cropping, neon glow on active */}
          <div
            role="tablist"
            className="relative flex items-stretch gap-1 lg:gap-2 border-b hairline"
          >
            {FEATURES.map((f, i) => (
              <button
                key={f.id}
                data-testid={`feature-tab-${f.id}`}
                role="tab"
                aria-selected={active === i}
                onClick={() => setActive(i)}
                className={`group relative flex-1 min-w-0 flex items-center justify-center gap-2 px-3 lg:px-4 py-3.5 transition-all duration-300 rounded-t-md ${
                  active === i
                    ? "text-[#1A3A61] bg-white"
                    : "text-[#1A3A61]/60 hover:text-[#1A3A61]/90"
                }`}
                style={
                  active === i
                    ? {
                        boxShadow:
                          "0 0 0 1px rgba(46,229,157,0.55), 0 0 24px -4px rgba(46,229,157,0.55), 0 0 42px -12px rgba(0,194,209,0.35)",
                      }
                    : undefined
                }
              >
                <span className="text-[12px] lg:text-[13px] leading-tight font-medium whitespace-nowrap">
                  {f.tab}
                </span>
                {f.tag && (
                  <span
                    data-testid={`feature-tag-${f.id}`}
                    className="flex-shrink-0 text-[8.5px] font-semibold tracking-[0.12em] px-1.5 py-0.5 bg-[#2EE59D] text-[#1A3A61] rounded-sm uppercase"
                  >
                    {f.tag}
                  </span>
                )}
                {active === i && (
                  <motion.span
                    layoutId="feat-tab-mark-h"
                    className="absolute left-3 right-3 bottom-[-1px] h-[2px] bg-[#2EE59D] rounded-full"
                    style={{
                      boxShadow:
                        "0 0 10px 0 rgba(46,229,157,0.85), 0 0 20px 0 rgba(46,229,157,0.55)",
                    }}
                    transition={{ duration: 0.35, ease: EASE }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Content: media + text below tabs — alternates each feature (even index = media right) */}
          <div className="mt-10 lg:mt-14 grid grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Media — big */}
            <div
              className={`col-span-12 lg:col-span-7 ${
                active % 2 === 0 ? "lg:order-2" : "lg:order-1"
              }`}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.5, ease: EASE }}
                >
                  <MediaFrame feature={feature} />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Text — large title, generous space */}
            <div
              className={`col-span-12 lg:col-span-5 lg:sticky lg:top-24 ${
                active % 2 === 0 ? "lg:order-1" : "lg:order-2"
              }`}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4, ease: EASE }}
                >
                  <h3 className="font-display text-[#1A3A61] text-3xl lg:text-[38px] leading-[1.08] tracking-[-0.02em]">
                    <FeatureTitle feature={feature} />
                  </h3>
                  <p className="mt-6 text-[#1A3A61]/72 text-[16px] lg:text-[17px] leading-[1.65]">
                    {feature.text}
                  </p>
                  <ul className="mt-8 space-y-3">
                    {feature.highlights.map((h) => (
                      <li
                        key={h}
                        className="flex items-start gap-3 text-[#1A3A61]/85 text-[14.5px]"
                      >
                        <Check
                          size={16}
                          className="mt-0.5 text-[#00C2D1] flex-shrink-0"
                          strokeWidth={2.5}
                        />
                        {h}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        {/* Mobile: accordion */}
        <ul className="md:hidden divide-y hairline border-y hairline">
          {FEATURES.map((f, i) => {
            const isOpen = active === i;
            return (
              <li key={f.id}>
                <button
                  data-testid={`feature-accordion-${f.id}`}
                  onClick={() => setActive(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left"
                >
                  <span className="flex items-baseline gap-3">
                    <span className="text-[10px] font-mono text-[#1A3A61]/40">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[16px] font-medium text-[#1A3A61]">{f.tab}</span>
                    {f.tag && (
                      <span className="text-[9px] font-semibold tracking-[0.12em] px-1.5 py-0.5 bg-[#2EE59D] text-[#1A3A61] rounded-sm uppercase">
                        {f.tag}
                      </span>
                    )}
                  </span>
                  <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }}>
                    <ChevronDown size={18} className="text-[#1A3A61]/60" />
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="pb-6">
                        <div className="mb-5">
                          <MediaFrame feature={f} />
                        </div>
                        <h4 className="font-display text-[#1A3A61] text-xl leading-tight">
                          <FeatureTitle feature={f} />
                        </h4>
                        <p className="mt-3 text-[#1A3A61]/70 text-[14px] leading-relaxed">
                          {f.text}
                        </p>
                        <ul className="mt-4 space-y-2">
                          {f.highlights.map((h) => (
                            <li key={h} className="flex items-start gap-2 text-[#1A3A61]/85 text-[14px]">
                              <Check size={14} className="mt-1 text-[#00C2D1] flex-shrink-0" strokeWidth={2.5} />
                              {h}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
